/**
 * EA Code Guide - Async Analysis Dashboard
 * Upload and analyze EA files (.mq5/.mqh) with real-time progress tracking
 *
 * Result Structure from DataFeed:
 * - summary, lineCount, fileSizeKB
 * - structure: { entryLogic, exitLogic, riskManagement, indicatorsUsed[], functions[] }
 * - bugs[]: { bugId, line, severity, category, title, description, suggestedFix }
 * - enhancements[]: { enhancementId, type, title, description, complexity }
 * - quality: { overallScore, readabilityScore, maintainabilityScore, errorHandlingScore, riskManagementScore }
 */

import React, { useState, useRef, useEffect, forwardRef, useCallback } from 'react';
import {
    Box,
    Paper,
    Typography,
    Button,
    LinearProgress,
    Alert,
    Chip,
    IconButton,
    Tooltip,
    CircularProgress,
    Collapse,
    Grid,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow
} from '@mui/material';
import {
    CloudUpload as CloudUploadIcon,
    Cancel as CancelIcon,
    Refresh as RefreshIcon,
    Download as DownloadIcon,
    CheckCircle as CheckCircleIcon,
    Error as ErrorIcon,
    HourglassEmpty as QueuedIcon,
    PlayArrow as ProcessingIcon,
    Code as CodeIcon,
    Description as DescriptionIcon,
    ExpandMore as ExpandMoreIcon,
    ExpandLess as ExpandLessIcon,
    BugReport as BugReportIcon,
    Lightbulb as LightbulbIcon,
    Assessment as AssessmentIcon,
    Functions as FunctionsIcon,
    Warning as WarningIcon,
    Info as InfoIcon
} from '@mui/icons-material';
import { toast } from 'react-toastify';

import {
    validateFileClient,
    startAsyncAnalysis,
    getJobStatus,
    cancelJob,
    downloadResults,
    parseJobStatus,
    checkEAHash,
    getAnalysisById,
    getMQLLibrary,
    loadLibraryAnalysis
} from '../../api/eaAnalysisApi';
import {
    subscribeToJob,
    unsubscribeFromJob,
    getConnectionState
} from '../../SignalR/eaProgressHub';
import { computeFileHash } from '../../utils/hashUtils';
import DuplicateEAModal from '../QAI/EAAssistant/DuplicateEAModal';
import MQLLibraryDropdown from '../QAI/EAAssistant/MQLLibraryDropdown';

// Embedded theme
const theme = {
    colors: {
        bg: { primary: '#191b20', secondary: '#292b2f', tertiary: '#333539', quaternary: '#3f4147' },
        text: { primary: 'rgba(255,255,255,1)', secondary: 'rgba(255,255,255,0.9)', tertiary: 'rgba(255,255,255,0.85)', quaternary: 'rgba(255,255,255,0.75)', placeholder: 'rgba(255,255,255,0.6)' },
        accent: { primary: '#7c3aed', secondary: '#5b21b6', tertiary: '#a78bfa' },
        border: { primary: 'rgba(255,255,255,0.1)', secondary: 'rgba(255,255,255,0.06)', tertiary: 'rgba(255,255,255,0.15)' },
        status: { success: '#22c55e', error: '#ef4444', warning: '#f59e0b', info: '#3b82f6' }
    },
    gradients: { primaryAccent: 'linear-gradient(135deg, #7c3aed 0%, #5b21b6 100%)' },
    borderRadius: { sm: '8px', md: '12px' }
};

// Severity color mapping
const getSeverityColor = (severity) => {
    switch (severity?.toLowerCase()) {
        case 'critical': return theme.colors.status.error;
        case 'error': return theme.colors.status.error;
        case 'warning': return theme.colors.status.warning;
        case 'info': return theme.colors.status.info;
        default: return theme.colors.text.quaternary;
    }
};

// Severity icon mapping
const getSeverityIcon = (severity) => {
    switch (severity?.toLowerCase()) {
        case 'critical':
        case 'error': return <ErrorIcon fontSize="small" />;
        case 'warning': return <WarningIcon fontSize="small" />;
        case 'info': return <InfoIcon fontSize="small" />;
        default: return <InfoIcon fontSize="small" />;
    }
};

// Complexity color mapping
const getComplexityColor = (complexity) => {
    switch (complexity?.toLowerCase()) {
        case 'simple': return theme.colors.status.success;
        case 'moderate': return theme.colors.status.warning;
        case 'complex': return theme.colors.status.error;
        default: return theme.colors.text.quaternary;
    }
};

// Score color helper
const getScoreColor = (score) => {
    if (score >= 8) return theme.colors.status.success;
    if (score >= 6) return theme.colors.status.warning;
    return theme.colors.status.error;
};

// Job status helpers
const getStatusIcon = (status) => {
    switch (status?.toLowerCase()) {
        case 'queued': return <QueuedIcon />;
        case 'processing': return <ProcessingIcon />;
        case 'completed': return <CheckCircleIcon />;
        case 'failed': return <ErrorIcon />;
        case 'cancelled': return <CancelIcon />;
        default: return <QueuedIcon />;
    }
};

const getStatusColor = (status) => {
    const parsed = parseJobStatus(status);
    switch (parsed.color) {
        case 'success': return theme.colors.status.success;
        case 'error': return theme.colors.status.error;
        case 'warning': return theme.colors.status.warning;
        case 'info': return theme.colors.status.info;
        default: return theme.colors.text.quaternary;
    }
};

const AdminEAAnalysisDashboard = forwardRef((props, ref) => {
    // File state
    const [selectedFile, setSelectedFile] = useState(null);
    const [fileValidation, setFileValidation] = useState(null);
    const [isDragOver, setIsDragOver] = useState(false);
    const fileInputRef = useRef(null);
    const [fileContent, setFileContent] = useState(null); // Store read file content

    // Job state
    const [currentJob, setCurrentJob] = useState(null);
    const [jobStatus, setJobStatus] = useState(null);
    const [progress, setProgress] = useState(0);
    const [results, setResults] = useState(null);
    const [error, setError] = useState(null);

    // UI state
    const [isUploading, setIsUploading] = useState(false);
    const [isCancelling, setIsCancelling] = useState(false);
    const [hubState, setHubState] = useState('Disconnected');
    const [expandedSections, setExpandedSections] = useState({
        summary: true,
        quality: true,
        bugs: true,
        enhancements: true,
        structure: false,
        functions: false
    });

    // P9-B: Duplicate detection state
    const [showDuplicateModal, setShowDuplicateModal] = useState(false);
    const [duplicateInfo, setDuplicateInfo] = useState(null);
    const [isCheckingHash, setIsCheckingHash] = useState(false);

    // P9-C: MQL Library state
    const [libraryItems, setLibraryItems] = useState([]);
    const [libraryGrouped, setLibraryGrouped] = useState({});
    const [libraryByType, setLibraryByType] = useState({});
    const [isLoadingLibrary, setIsLoadingLibrary] = useState(false);
    const [libraryError, setLibraryError] = useState(null);

    // Poll for hub connection state
    useEffect(() => {
        const interval = setInterval(() => {
            setHubState(getConnectionState());
        }, 2000);
        return () => clearInterval(interval);
    }, []);

    // Cleanup on unmount
    useEffect(() => {
        return () => {
            if (currentJob?.jobId) {
                unsubscribeFromJob(currentJob.jobId);
            }
        };
    }, [currentJob?.jobId]);

    // ═══════════════════════════════════════════════════════════════════════════
    // FILE HANDLING
    // ═══════════════════════════════════════════════════════════════════════════

    const handleFileSelect = (file) => {
        if (!file) return;
        const validation = validateFileClient(file);
        setFileValidation(validation);
        if (validation.valid) {
            setSelectedFile(file);
            setError(null);
            // Read file content for analysis
            const reader = new FileReader();
            reader.onload = (event) => {
                setFileContent(event.target.result);
            };
            reader.onerror = () => {
                setError('Failed to read file content');
                setFileContent(null);
            };
            reader.readAsText(file);
            toast.success(`File selected: ${file.name}`);
        } else {
            setSelectedFile(null);
            setFileContent(null);
            setError(validation.errors.join('. '));
        }
    };

    const handleInputChange = (event) => {
        const file = event.target.files?.[0];
        handleFileSelect(file);
    };

    const handleDrop = (event) => {
        event.preventDefault();
        setIsDragOver(false);
        const file = event.dataTransfer?.files?.[0];
        handleFileSelect(file);
    };

    const handleDragOver = (event) => {
        event.preventDefault();
        setIsDragOver(true);
    };

    const handleDragLeave = (event) => {
        event.preventDefault();
        setIsDragOver(false);
    };

    const clearFile = () => {
        setSelectedFile(null);
        setFileValidation(null);
        setFileContent(null);
        if (fileInputRef.current) {
            fileInputRef.current.value = '';
        }
    };

    // ═══════════════════════════════════════════════════════════════════════════
    // ANALYSIS OPERATIONS
    // ═══════════════════════════════════════════════════════════════════════════

    // Get userId from localStorage (Admin panel uses main token)
    const getUserId = () => {
        try {
            const token = localStorage.getItem('token');
            if (token) {
                const payload = JSON.parse(atob(token.split('.')[1]));
                return payload.userId || payload.sub || payload.nameid;
            }
        } catch (e) {
            console.warn('Failed to parse userId from token:', e);
        }
        return null;
    };

    // P9-B: Check for duplicate before starting analysis
    const handleStartAnalysis = async () => {
        if (!selectedFile || !fileContent) {
            toast.error('Please select a file first');
            return;
        }

        const userId = getUserId();
        if (!userId) {
            toast.error('User not authenticated');
            return;
        }

        setIsCheckingHash(true);
        setError(null);

        try {
            // Compute hash of file content
            const hash = await computeFileHash(selectedFile);
            console.log('[AdminEA] Computed hash:', hash);

            // Check if duplicate exists
            const hashResult = await checkEAHash(userId, hash);
            console.log('[AdminEA] Hash check result:', hashResult);

            if (hashResult.success && hashResult.data?.exists) {
                // Duplicate found - show modal
                setDuplicateInfo(hashResult.data);
                setShowDuplicateModal(true);
                setIsCheckingHash(false);
                return;
            }

            // No duplicate - proceed with analysis
            setIsCheckingHash(false);
            await proceedWithAnalysis();
        } catch (err) {
            console.error('[AdminEA] Hash check error:', err);
            setIsCheckingHash(false);
            // On hash check failure, proceed with analysis anyway
            await proceedWithAnalysis();
        }
    };

    // P9-B: Use existing analysis from duplicate detection
    const handleUseExisting = async () => {
        if (!duplicateInfo?.analysisId) {
            toast.error('No existing analysis found');
            return;
        }

        setIsUploading(true);
        setShowDuplicateModal(false);

        try {
            const result = await getAnalysisById(duplicateInfo.analysisId);
            if (result.success && result.data) {
                setResults(result.data);
                setJobStatus('completed');
                setCurrentJob({ jobId: duplicateInfo.analysisId, fileName: selectedFile?.name || duplicateInfo.eaName });
                toast.success('Loaded existing analysis (0 tokens used)');
            } else {
                throw new Error(result.error || 'Failed to load existing analysis');
            }
        } catch (err) {
            toast.error(err.message);
            setError(err.message);
        } finally {
            setIsUploading(false);
            setDuplicateInfo(null);
        }
    };

    // P9-B: Proceed with re-analysis
    const handleReanalyze = () => {
        setShowDuplicateModal(false);
        setDuplicateInfo(null);
        proceedWithAnalysis();
    };

    // Actual analysis function (after hash check)
    const proceedWithAnalysis = async () => {
        if (!selectedFile || !fileContent) {
            toast.error('Please select a file first');
            return;
        }

        setIsUploading(true);
        setError(null);
        setProgress(0);
        setResults(null);
        setJobStatus('uploading');

        try {
            // Build proper analysis payload with file content
            const analysisPayload = {
                name: selectedFile.name,
                mainSource: fileContent,
                consentGiven: true // Admin panel assumes consent
            };

            const result = await startAsyncAnalysis(analysisPayload);
            if (!result.success) {
                throw new Error(result.error || 'Failed to start analysis');
            }

            const { jobId, queuePosition } = result.data;
            setCurrentJob({ jobId, queuePosition, fileName: selectedFile.name });
            setJobStatus('queued');
            toast.info(`Analysis queued (Position: ${queuePosition || 1})`);

            const subscribed = await subscribeToJob(jobId, {
                onProgress: (prog) => {
                    setProgress(prog);
                    setJobStatus('processing');
                },
                onComplete: (jobResult) => {
                    setProgress(100);
                    setJobStatus('completed');
                    setResults(jobResult);
                    toast.success('Analysis completed!');
                    unsubscribeFromJob(jobId);
                    // Refresh library after new analysis
                    loadLibrary();
                },
                onError: (err) => {
                    setJobStatus('failed');
                    setError(err || 'Analysis failed');
                    toast.error('Analysis failed');
                    unsubscribeFromJob(jobId);
                },
                onCancelled: () => {
                    setJobStatus('cancelled');
                    toast.info('Analysis cancelled');
                    unsubscribeFromJob(jobId);
                }
            });

            if (!subscribed) {
                console.warn('Failed to subscribe to SignalR, falling back to polling');
                startPolling(jobId);
            }
        } catch (err) {
            setError(err.message);
            setJobStatus(null);
            toast.error(err.message);
        } finally {
            setIsUploading(false);
        }
    };

    const startPolling = (jobId) => {
        const pollInterval = setInterval(async () => {
            const result = await getJobStatus(jobId);
            if (result.success) {
                const { status, progress: prog, result: jobResult, error: jobError } = result.data;
                setJobStatus(status);
                if (prog !== undefined) setProgress(prog);
                if (status === 'completed') {
                    setResults(jobResult);
                    toast.success('Analysis completed!');
                    clearInterval(pollInterval);
                } else if (status === 'failed') {
                    setError(jobError || 'Analysis failed');
                    toast.error('Analysis failed');
                    clearInterval(pollInterval);
                } else if (status === 'cancelled') {
                    toast.info('Analysis cancelled');
                    clearInterval(pollInterval);
                }
            }
        }, 2000);
        return () => clearInterval(pollInterval);
    };

    const handleCancelJob = async () => {
        if (!currentJob?.jobId) return;
        setIsCancelling(true);
        try {
            const result = await cancelJob(currentJob.jobId);
            if (result.success) {
                setJobStatus('cancelled');
                toast.info('Job cancelled');
            } else {
                toast.error(result.error || 'Failed to cancel job');
            }
        } catch (err) {
            toast.error('Failed to cancel job');
        } finally {
            setIsCancelling(false);
        }
    };

    const handleRefreshStatus = async () => {
        if (!currentJob?.jobId) return;
        const result = await getJobStatus(currentJob.jobId);
        if (result.success) {
            const { status, progress: prog, result: jobResult, error: jobError } = result.data;
            setJobStatus(status);
            if (prog !== undefined) setProgress(prog);
            if (jobResult) setResults(jobResult);
            if (jobError) setError(jobError);
        }
    };

    const handleDownloadResults = () => {
        if (results) {
            const filename = `ea-analysis-${currentJob?.fileName || 'results'}-${Date.now()}.json`;
            downloadResults(results, filename);
            toast.success('Results downloaded');
        }
    };

    const handleReset = () => {
        if (currentJob?.jobId) {
            unsubscribeFromJob(currentJob.jobId);
        }
        setSelectedFile(null);
        setFileValidation(null);
        setFileContent(null);
        setCurrentJob(null);
        setJobStatus(null);
        setProgress(0);
        setResults(null);
        setError(null);
        setDuplicateInfo(null);
        setShowDuplicateModal(false);
        if (fileInputRef.current) {
            fileInputRef.current.value = '';
        }
    };

    const toggleSection = (section) => {
        setExpandedSections(prev => ({ ...prev, [section]: !prev[section] }));
    };

    // ═══════════════════════════════════════════════════════════════════════════
    // P9-C: MQL LIBRARY OPERATIONS
    // ═══════════════════════════════════════════════════════════════════════════

    // Load user's MQL library
    const loadLibrary = useCallback(async () => {
        const userId = getUserId();
        if (!userId) return;

        setIsLoadingLibrary(true);
        setLibraryError(null);

        try {
            const result = await getMQLLibrary(userId, { limit: 50 });
            if (result.success) {
                setLibraryItems(result.data || []);
                setLibraryGrouped(result.grouped || {});
                setLibraryByType(result.byType || {});
            } else {
                setLibraryError(result.error || 'Failed to load library');
            }
        } catch (err) {
            console.error('[AdminEA] Library load error:', err);
            setLibraryError(err.message || 'Failed to load library');
        } finally {
            setIsLoadingLibrary(false);
        }
    }, []);

    // Load analysis from library selection
    const handleLibrarySelect = async (item) => {
        if (!item?.latestAnalysisId) {
            toast.error('No analysis available for this item');
            return;
        }

        setIsUploading(true);
        setError(null);

        try {
            const result = await loadLibraryAnalysis(item.latestAnalysisId);
            if (result.success && result.data) {
                setResults(result.data);
                setJobStatus('completed');
                setCurrentJob({ jobId: item.latestAnalysisId, fileName: item.name });
                toast.success(`Loaded "${item.name}" from library (0 tokens)`);
            } else {
                throw new Error(result.error || 'Failed to load analysis');
            }
        } catch (err) {
            toast.error(err.message);
            setError(err.message);
        } finally {
            setIsUploading(false);
        }
    };

    // Load library on mount
    useEffect(() => {
        loadLibrary();
    }, [loadLibrary]);

    // ═══════════════════════════════════════════════════════════════════════════
    // RENDER HELPERS
    // ═══════════════════════════════════════════════════════════════════════════

    const isProcessing = ['queued', 'processing', 'uploading'].includes(jobStatus);
    const isComplete = ['completed', 'failed', 'cancelled'].includes(jobStatus);

    // Quality Score Card Component
    const ScoreCard = ({ label, score }) => (
        <Box sx={{ textAlign: 'center', p: 1.5, background: theme.colors.bg.tertiary, borderRadius: theme.borderRadius.sm, flex: 1, minWidth: 100 }}>
            <Typography sx={{ color: theme.colors.text.quaternary, fontSize: '0.75rem', mb: 0.5 }}>{label}</Typography>
            <Typography sx={{ color: getScoreColor(score || 0), fontWeight: 700, fontSize: '1.25rem' }}>
                {score?.toFixed(1) || 'N/A'}
            </Typography>
        </Box>
    );

    // Bug Item Component
    const BugItem = ({ bug }) => (
        <Paper sx={{ p: 2, mb: 1.5, background: theme.colors.bg.tertiary, borderRadius: theme.borderRadius.sm, borderLeft: `3px solid ${getSeverityColor(bug.severity)}` }}>
            <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1.5 }}>
                <Box sx={{ color: getSeverityColor(bug.severity), mt: 0.25 }}>
                    {getSeverityIcon(bug.severity)}
                </Box>
                <Box sx={{ flex: 1 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
                        <Typography sx={{ color: theme.colors.text.primary, fontWeight: 600, fontSize: '0.875rem' }}>
                            {bug.title}
                        </Typography>
                        <Chip label={bug.severity} size="small" sx={{ background: `${getSeverityColor(bug.severity)}20`, color: getSeverityColor(bug.severity), fontSize: '0.7rem', height: 20 }} />
                        <Chip label={bug.category} size="small" sx={{ background: theme.colors.bg.quaternary, color: theme.colors.text.quaternary, fontSize: '0.7rem', height: 20 }} />
                        {bug.line && <Chip label={`Line ${bug.line}`} size="small" sx={{ background: theme.colors.accent.primary + '20', color: theme.colors.accent.tertiary, fontSize: '0.7rem', height: 20 }} />}
                    </Box>
                    <Typography sx={{ color: theme.colors.text.tertiary, fontSize: '0.8rem', mb: 1 }}>
                        {bug.description}
                    </Typography>
                    {bug.codeSnippet && (
                        <Box sx={{ background: theme.colors.bg.primary, p: 1, borderRadius: 1, mb: 1, fontFamily: 'monospace', fontSize: '0.75rem', color: theme.colors.accent.tertiary, overflow: 'auto' }}>
                            {bug.codeSnippet}
                        </Box>
                    )}
                    {bug.suggestedFix && (
                        <Box sx={{ background: `${theme.colors.status.success}10`, p: 1.5, borderRadius: 1, border: `1px solid ${theme.colors.status.success}30` }}>
                            <Typography sx={{ color: theme.colors.status.success, fontSize: '0.75rem', fontWeight: 600, mb: 0.5 }}>Suggested Fix:</Typography>
                            <Typography sx={{ color: theme.colors.text.secondary, fontSize: '0.8rem', fontFamily: 'monospace' }}>
                                {bug.suggestedFix}
                            </Typography>
                        </Box>
                    )}
                </Box>
            </Box>
        </Paper>
    );

    // Enhancement Item Component
    const EnhancementItem = ({ enhancement }) => (
        <Paper sx={{ p: 2, mb: 1.5, background: theme.colors.bg.tertiary, borderRadius: theme.borderRadius.sm, borderLeft: `3px solid ${theme.colors.accent.primary}` }}>
            <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1.5 }}>
                <LightbulbIcon sx={{ color: theme.colors.accent.tertiary, mt: 0.25 }} fontSize="small" />
                <Box sx={{ flex: 1 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
                        <Typography sx={{ color: theme.colors.text.primary, fontWeight: 600, fontSize: '0.875rem' }}>
                            {enhancement.title}
                        </Typography>
                        <Chip label={enhancement.type?.replace(/_/g, ' ')} size="small" sx={{ background: theme.colors.accent.primary + '20', color: theme.colors.accent.tertiary, fontSize: '0.7rem', height: 20 }} />
                        <Chip label={enhancement.complexity} size="small" sx={{ background: `${getComplexityColor(enhancement.complexity)}20`, color: getComplexityColor(enhancement.complexity), fontSize: '0.7rem', height: 20 }} />
                    </Box>
                    <Typography sx={{ color: theme.colors.text.tertiary, fontSize: '0.8rem' }}>
                        {enhancement.description}
                    </Typography>
                    {enhancement.estimatedHours && (
                        <Typography sx={{ color: theme.colors.text.quaternary, fontSize: '0.75rem', mt: 1 }}>
                            Estimated effort: ~{enhancement.estimatedHours} hours
                        </Typography>
                    )}
                </Box>
            </Box>
        </Paper>
    );

    // ═══════════════════════════════════════════════════════════════════════════
    // RENDER
    // ═══════════════════════════════════════════════════════════════════════════

    return (
        <Box ref={ref} sx={{ p: 3, maxWidth: 1200, mx: 'auto' }}>
            {/* Header */}
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
                <Box>
                    <Typography variant="h5" sx={{ color: theme.colors.text.primary, fontWeight: 600, display: 'flex', alignItems: 'center', gap: 1 }}>
                        <CodeIcon sx={{ color: theme.colors.accent.primary }} />
                        EA Code Guide
                    </Typography>
                    <Typography sx={{ color: theme.colors.text.quaternary, mt: 0.5 }}>
                        Upload and analyze MQL5 Expert Advisors with AI-powered insights
                    </Typography>
                </Box>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                    <Chip size="small" label={hubState} color={hubState === 'Connected' ? 'success' : hubState === 'Connecting' ? 'warning' : 'default'} sx={{ fontSize: '0.75rem' }} />
                    {isComplete && (
                        <Button variant="outlined" size="small" startIcon={<RefreshIcon />} onClick={handleReset} sx={{ color: theme.colors.accent.tertiary, borderColor: theme.colors.accent.tertiary }}>
                            New Analysis
                        </Button>
                    )}
                </Box>
            </Box>

            {/* Error Alert */}
            {error && (
                <Alert severity="error" onClose={() => setError(null)} sx={{ mb: 3, background: `${theme.colors.status.error}15`, color: theme.colors.status.error }}>
                    {error}
                </Alert>
            )}

            {/* Upload Section */}
            {!currentJob && (
                <Paper sx={{ p: 3, mb: 3, background: theme.colors.bg.secondary, border: `1px solid ${theme.colors.border.primary}` }}>
                    {/* Header with Library Dropdown */}
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                        <Typography sx={{ color: theme.colors.text.primary, fontWeight: 600 }}>Upload EA File</Typography>
                        {/* P9-C: MQL Library Dropdown */}
                        <MQLLibraryDropdown
                            libraryItems={libraryItems}
                            grouped={libraryGrouped}
                            byType={libraryByType}
                            onSelect={handleLibrarySelect}
                            isLoading={isLoadingLibrary}
                            error={libraryError}
                            onRefresh={loadLibrary}
                            disabled={isUploading || isCheckingHash}
                        />
                    </Box>

                    <Box
                        onDrop={handleDrop}
                        onDragOver={handleDragOver}
                        onDragLeave={handleDragLeave}
                        onClick={() => fileInputRef.current?.click()}
                        sx={{
                            p: 4, border: `2px dashed ${isDragOver ? theme.colors.accent.primary : theme.colors.border.tertiary}`,
                            borderRadius: theme.borderRadius.md, background: isDragOver ? `${theme.colors.accent.primary}10` : theme.colors.bg.tertiary,
                            textAlign: 'center', cursor: 'pointer', transition: 'all 0.2s',
                            '&:hover': { borderColor: theme.colors.accent.primary, background: `${theme.colors.accent.primary}10` }
                        }}
                    >
                        <CloudUploadIcon sx={{ fontSize: 48, color: isDragOver ? theme.colors.accent.primary : theme.colors.text.quaternary, mb: 1 }} />
                        <Typography sx={{ color: theme.colors.text.secondary, fontWeight: 500 }}>Drag & drop your EA file here</Typography>
                        <Typography sx={{ color: theme.colors.text.quaternary, fontSize: '0.875rem', mt: 0.5 }}>or click to browse</Typography>
                        <Typography sx={{ color: theme.colors.text.placeholder, fontSize: '0.75rem', mt: 1 }}>Supported: .mq5, .mqh (max 500KB)</Typography>
                    </Box>
                    <input ref={fileInputRef} type="file" accept=".mq5,.mqh" onChange={handleInputChange} style={{ display: 'none' }} />

                    {selectedFile && fileValidation?.valid && (
                        <Box sx={{ mt: 2, p: 2, background: theme.colors.bg.tertiary, borderRadius: theme.borderRadius.sm, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                                <DescriptionIcon sx={{ color: theme.colors.accent.primary }} />
                                <Box>
                                    <Typography sx={{ color: theme.colors.text.primary, fontWeight: 500 }}>{selectedFile.name}</Typography>
                                    <Typography sx={{ color: theme.colors.text.quaternary, fontSize: '0.75rem' }}>{fileValidation.fileInfo.size} • {fileValidation.fileInfo.type}</Typography>
                                </Box>
                            </Box>
                            <IconButton size="small" onClick={clearFile} sx={{ color: theme.colors.text.quaternary }}><CancelIcon fontSize="small" /></IconButton>
                        </Box>
                    )}

                    <Box sx={{ mt: 3, display: 'flex', justifyContent: 'flex-end' }}>
                        <Button variant="contained" size="large" onClick={handleStartAnalysis} disabled={!selectedFile || !fileValidation?.valid || !fileContent || isUploading || isCheckingHash}
                            startIcon={(isUploading || isCheckingHash) ? <CircularProgress size={20} color="inherit" /> : <PlayArrow />}
                            sx={{ background: theme.gradients.primaryAccent, px: 4 }}>
                            {isCheckingHash ? 'Checking...' : isUploading ? 'Uploading...' : 'Start Analysis'}
                        </Button>
                    </Box>
                </Paper>
            )}

            {/* Progress Section */}
            {currentJob && isProcessing && (
                <Paper sx={{ p: 3, mb: 3, background: theme.colors.bg.secondary, border: `1px solid ${theme.colors.border.primary}` }}>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                            <Box sx={{ width: 40, height: 40, borderRadius: '50%', background: `${getStatusColor(jobStatus)}20`, display: 'flex', alignItems: 'center', justifyContent: 'center', color: getStatusColor(jobStatus) }}>
                                {jobStatus === 'processing' ? <CircularProgress size={24} color="inherit" /> : getStatusIcon(jobStatus)}
                            </Box>
                            <Box>
                                <Typography sx={{ color: theme.colors.text.primary, fontWeight: 600 }}>{parseJobStatus(jobStatus).label}</Typography>
                                <Typography sx={{ color: theme.colors.text.quaternary, fontSize: '0.75rem' }}>{currentJob.fileName} • Job ID: {currentJob.jobId}</Typography>
                            </Box>
                        </Box>
                        <Box sx={{ display: 'flex', gap: 1 }}>
                            <Tooltip title="Refresh Status"><IconButton size="small" onClick={handleRefreshStatus} sx={{ color: theme.colors.text.tertiary }}><RefreshIcon fontSize="small" /></IconButton></Tooltip>
                            <Tooltip title="Cancel Job"><IconButton size="small" onClick={handleCancelJob} disabled={isCancelling} sx={{ color: theme.colors.status.error }}>{isCancelling ? <CircularProgress size={18} /> : <CancelIcon fontSize="small" />}</IconButton></Tooltip>
                        </Box>
                    </Box>
                    <Box sx={{ mt: 2 }}>
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                            <Typography sx={{ color: theme.colors.text.tertiary, fontSize: '0.875rem' }}>Processing chunks...</Typography>
                            <Typography sx={{ color: theme.colors.accent.primary, fontWeight: 600 }}>{progress}%</Typography>
                        </Box>
                        <LinearProgress variant="determinate" value={progress} sx={{ height: 8, borderRadius: 4, background: theme.colors.bg.tertiary, '& .MuiLinearProgress-bar': { borderRadius: 4, background: theme.gradients.primaryAccent } }} />
                    </Box>
                    {currentJob.queuePosition > 1 && jobStatus === 'queued' && (
                        <Alert severity="info" sx={{ mt: 2, background: `${theme.colors.status.info}15` }}>Queue position: {currentJob.queuePosition}</Alert>
                    )}
                </Paper>
            )}

            {/* Results Section */}
            {results && jobStatus === 'completed' && (
                <Box>
                    {/* Header */}
                    <Paper sx={{ p: 3, mb: 2, background: theme.colors.bg.secondary, border: `1px solid ${theme.colors.border.primary}` }}>
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                                <Box sx={{ width: 40, height: 40, borderRadius: '50%', background: `${theme.colors.status.success}20`, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                                    <CheckCircleIcon sx={{ color: theme.colors.status.success }} />
                                </Box>
                                <Box>
                                    <Typography sx={{ color: theme.colors.text.primary, fontWeight: 600 }}>Analysis Complete</Typography>
                                    <Typography sx={{ color: theme.colors.text.quaternary, fontSize: '0.75rem' }}>
                                        {currentJob?.fileName} • {results.lineCount || 0} lines • {results.fileSizeKB || 0}KB
                                    </Typography>
                                </Box>
                            </Box>
                            <Button variant="outlined" size="small" startIcon={<DownloadIcon />} onClick={handleDownloadResults} sx={{ color: theme.colors.accent.tertiary, borderColor: theme.colors.accent.tertiary }}>
                                Download JSON
                            </Button>
                        </Box>
                    </Paper>

                    {/* Quality Metrics */}
                    {results.quality && (
                        <Paper sx={{ p: 3, mb: 2, background: theme.colors.bg.secondary, border: `1px solid ${theme.colors.border.primary}` }}>
                            <Box onClick={() => toggleSection('quality')} sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', cursor: 'pointer', mb: expandedSections.quality ? 2 : 0 }}>
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                    <AssessmentIcon sx={{ color: theme.colors.accent.primary }} />
                                    <Typography sx={{ color: theme.colors.text.primary, fontWeight: 600 }}>Quality Metrics</Typography>
                                    <Chip label={`Overall: ${results.quality.overallScore?.toFixed(1) || 'N/A'}`} size="small" sx={{ ml: 1, background: `${getScoreColor(results.quality.overallScore || 0)}20`, color: getScoreColor(results.quality.overallScore || 0) }} />
                                </Box>
                                <IconButton size="small" sx={{ color: theme.colors.text.quaternary }}>{expandedSections.quality ? <ExpandLessIcon /> : <ExpandMoreIcon />}</IconButton>
                            </Box>
                            <Collapse in={expandedSections.quality}>
                                <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
                                    <ScoreCard label="Readability" score={results.quality.readabilityScore} />
                                    <ScoreCard label="Maintainability" score={results.quality.maintainabilityScore} />
                                    <ScoreCard label="Error Handling" score={results.quality.errorHandlingScore} />
                                    <ScoreCard label="Risk Management" score={results.quality.riskManagementScore} />
                                </Box>
                                {results.quality.complexity && (
                                    <Box sx={{ mt: 2, display: 'flex', alignItems: 'center', gap: 1 }}>
                                        <Typography sx={{ color: theme.colors.text.quaternary, fontSize: '0.875rem' }}>Complexity:</Typography>
                                        <Chip label={results.quality.complexity} size="small" sx={{ background: `${getComplexityColor(results.quality.complexity)}20`, color: getComplexityColor(results.quality.complexity) }} />
                                    </Box>
                                )}
                            </Collapse>
                        </Paper>
                    )}

                    {/* Summary */}
                    {results.summary && (
                        <Paper sx={{ p: 3, mb: 2, background: theme.colors.bg.secondary, border: `1px solid ${theme.colors.border.primary}` }}>
                            <Box onClick={() => toggleSection('summary')} sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', cursor: 'pointer', mb: expandedSections.summary ? 2 : 0 }}>
                                <Typography sx={{ color: theme.colors.text.primary, fontWeight: 600 }}>Summary</Typography>
                                <IconButton size="small" sx={{ color: theme.colors.text.quaternary }}>{expandedSections.summary ? <ExpandLessIcon /> : <ExpandMoreIcon />}</IconButton>
                            </Box>
                            <Collapse in={expandedSections.summary}>
                                <Typography sx={{ color: theme.colors.text.secondary, lineHeight: 1.7 }}>{results.summary}</Typography>
                            </Collapse>
                        </Paper>
                    )}

                    {/* Bugs Section */}
                    {results.bugs && results.bugs.length > 0 && (
                        <Paper sx={{ p: 3, mb: 2, background: theme.colors.bg.secondary, border: `1px solid ${theme.colors.border.primary}` }}>
                            <Box onClick={() => toggleSection('bugs')} sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', cursor: 'pointer', mb: expandedSections.bugs ? 2 : 0 }}>
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                    <BugReportIcon sx={{ color: theme.colors.status.error }} />
                                    <Typography sx={{ color: theme.colors.text.primary, fontWeight: 600 }}>Issues Found</Typography>
                                    <Chip label={results.bugs.length} size="small" sx={{ ml: 1, background: `${theme.colors.status.error}20`, color: theme.colors.status.error }} />
                                </Box>
                                <IconButton size="small" sx={{ color: theme.colors.text.quaternary }}>{expandedSections.bugs ? <ExpandLessIcon /> : <ExpandMoreIcon />}</IconButton>
                            </Box>
                            <Collapse in={expandedSections.bugs}>
                                {results.bugs.map((bug, idx) => <BugItem key={bug.bugId || idx} bug={bug} />)}
                            </Collapse>
                        </Paper>
                    )}

                    {/* Enhancements Section */}
                    {results.enhancements && results.enhancements.length > 0 && (
                        <Paper sx={{ p: 3, mb: 2, background: theme.colors.bg.secondary, border: `1px solid ${theme.colors.border.primary}` }}>
                            <Box onClick={() => toggleSection('enhancements')} sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', cursor: 'pointer', mb: expandedSections.enhancements ? 2 : 0 }}>
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                    <LightbulbIcon sx={{ color: theme.colors.accent.tertiary }} />
                                    <Typography sx={{ color: theme.colors.text.primary, fontWeight: 600 }}>Suggested Enhancements</Typography>
                                    <Chip label={results.enhancements.length} size="small" sx={{ ml: 1, background: `${theme.colors.accent.primary}20`, color: theme.colors.accent.tertiary }} />
                                </Box>
                                <IconButton size="small" sx={{ color: theme.colors.text.quaternary }}>{expandedSections.enhancements ? <ExpandLessIcon /> : <ExpandMoreIcon />}</IconButton>
                            </Box>
                            <Collapse in={expandedSections.enhancements}>
                                {results.enhancements.map((enh, idx) => <EnhancementItem key={enh.enhancementId || idx} enhancement={enh} />)}
                            </Collapse>
                        </Paper>
                    )}

                    {/* Structure Section */}
                    {results.structure && (
                        <Paper sx={{ p: 3, mb: 2, background: theme.colors.bg.secondary, border: `1px solid ${theme.colors.border.primary}` }}>
                            <Box onClick={() => toggleSection('structure')} sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', cursor: 'pointer', mb: expandedSections.structure ? 2 : 0 }}>
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                    <CodeIcon sx={{ color: theme.colors.accent.primary }} />
                                    <Typography sx={{ color: theme.colors.text.primary, fontWeight: 600 }}>EA Structure</Typography>
                                </Box>
                                <IconButton size="small" sx={{ color: theme.colors.text.quaternary }}>{expandedSections.structure ? <ExpandLessIcon /> : <ExpandMoreIcon />}</IconButton>
                            </Box>
                            <Collapse in={expandedSections.structure}>
                                <Grid container spacing={2}>
                                    {results.structure.entryLogic && (
                                        <Grid item xs={12} md={6}>
                                            <Box sx={{ p: 2, background: theme.colors.bg.tertiary, borderRadius: theme.borderRadius.sm }}>
                                                <Typography sx={{ color: theme.colors.accent.tertiary, fontSize: '0.75rem', fontWeight: 600, mb: 0.5 }}>Entry Logic</Typography>
                                                <Typography sx={{ color: theme.colors.text.secondary, fontSize: '0.875rem' }}>{results.structure.entryLogic}</Typography>
                                            </Box>
                                        </Grid>
                                    )}
                                    {results.structure.exitLogic && (
                                        <Grid item xs={12} md={6}>
                                            <Box sx={{ p: 2, background: theme.colors.bg.tertiary, borderRadius: theme.borderRadius.sm }}>
                                                <Typography sx={{ color: theme.colors.accent.tertiary, fontSize: '0.75rem', fontWeight: 600, mb: 0.5 }}>Exit Logic</Typography>
                                                <Typography sx={{ color: theme.colors.text.secondary, fontSize: '0.875rem' }}>{results.structure.exitLogic}</Typography>
                                            </Box>
                                        </Grid>
                                    )}
                                    {results.structure.riskManagement && (
                                        <Grid item xs={12} md={6}>
                                            <Box sx={{ p: 2, background: theme.colors.bg.tertiary, borderRadius: theme.borderRadius.sm }}>
                                                <Typography sx={{ color: theme.colors.accent.tertiary, fontSize: '0.75rem', fontWeight: 600, mb: 0.5 }}>Risk Management</Typography>
                                                <Typography sx={{ color: theme.colors.text.secondary, fontSize: '0.875rem' }}>{results.structure.riskManagement}</Typography>
                                            </Box>
                                        </Grid>
                                    )}
                                    {results.structure.orderManagement && (
                                        <Grid item xs={12} md={6}>
                                            <Box sx={{ p: 2, background: theme.colors.bg.tertiary, borderRadius: theme.borderRadius.sm }}>
                                                <Typography sx={{ color: theme.colors.accent.tertiary, fontSize: '0.75rem', fontWeight: 600, mb: 0.5 }}>Order Management</Typography>
                                                <Typography sx={{ color: theme.colors.text.secondary, fontSize: '0.875rem' }}>{results.structure.orderManagement}</Typography>
                                            </Box>
                                        </Grid>
                                    )}
                                    {results.structure.indicatorsUsed && results.structure.indicatorsUsed.length > 0 && (
                                        <Grid item xs={12}>
                                            <Box sx={{ p: 2, background: theme.colors.bg.tertiary, borderRadius: theme.borderRadius.sm }}>
                                                <Typography sx={{ color: theme.colors.accent.tertiary, fontSize: '0.75rem', fontWeight: 600, mb: 1 }}>Indicators Used</Typography>
                                                <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                                                    {results.structure.indicatorsUsed.map((ind, idx) => (
                                                        <Chip key={idx} label={ind} size="small" sx={{ background: theme.colors.bg.quaternary, color: theme.colors.text.secondary }} />
                                                    ))}
                                                </Box>
                                            </Box>
                                        </Grid>
                                    )}
                                </Grid>
                            </Collapse>
                        </Paper>
                    )}

                    {/* Functions Section */}
                    {results.structure?.functions && results.structure.functions.length > 0 && (
                        <Paper sx={{ p: 3, mb: 2, background: theme.colors.bg.secondary, border: `1px solid ${theme.colors.border.primary}` }}>
                            <Box onClick={() => toggleSection('functions')} sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', cursor: 'pointer', mb: expandedSections.functions ? 2 : 0 }}>
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                    <FunctionsIcon sx={{ color: theme.colors.accent.primary }} />
                                    <Typography sx={{ color: theme.colors.text.primary, fontWeight: 600 }}>Functions Detected</Typography>
                                    <Chip label={results.structure.functions.length} size="small" sx={{ ml: 1, background: theme.colors.bg.quaternary, color: theme.colors.text.quaternary }} />
                                </Box>
                                <IconButton size="small" sx={{ color: theme.colors.text.quaternary }}>{expandedSections.functions ? <ExpandLessIcon /> : <ExpandMoreIcon />}</IconButton>
                            </Box>
                            <Collapse in={expandedSections.functions}>
                                <TableContainer>
                                    <Table size="small">
                                        <TableHead>
                                            <TableRow>
                                                <TableCell sx={{ color: theme.colors.text.quaternary, borderBottom: `1px solid ${theme.colors.border.secondary}` }}>Function</TableCell>
                                                <TableCell sx={{ color: theme.colors.text.quaternary, borderBottom: `1px solid ${theme.colors.border.secondary}` }}>Type</TableCell>
                                                <TableCell sx={{ color: theme.colors.text.quaternary, borderBottom: `1px solid ${theme.colors.border.secondary}` }}>Lines</TableCell>
                                                <TableCell sx={{ color: theme.colors.text.quaternary, borderBottom: `1px solid ${theme.colors.border.secondary}` }}>Description</TableCell>
                                            </TableRow>
                                        </TableHead>
                                        <TableBody>
                                            {results.structure.functions.map((fn, idx) => (
                                                <TableRow key={idx} hover sx={{ '&:hover': { background: theme.colors.bg.tertiary } }}>
                                                    <TableCell sx={{ color: theme.colors.accent.tertiary, fontFamily: 'monospace', borderBottom: `1px solid ${theme.colors.border.secondary}` }}>{fn.name}</TableCell>
                                                    <TableCell sx={{ borderBottom: `1px solid ${theme.colors.border.secondary}` }}>
                                                        <Chip label={fn.type} size="small" sx={{ background: theme.colors.bg.quaternary, color: theme.colors.text.tertiary, fontSize: '0.7rem' }} />
                                                    </TableCell>
                                                    <TableCell sx={{ color: theme.colors.text.quaternary, borderBottom: `1px solid ${theme.colors.border.secondary}` }}>{fn.startLine}-{fn.endLine}</TableCell>
                                                    <TableCell sx={{ color: theme.colors.text.secondary, fontSize: '0.8rem', borderBottom: `1px solid ${theme.colors.border.secondary}` }}>{fn.description}</TableCell>
                                                </TableRow>
                                            ))}
                                        </TableBody>
                                    </Table>
                                </TableContainer>
                            </Collapse>
                        </Paper>
                    )}

                    {/* Raw Results Fallback */}
                    {!results.summary && !results.bugs && !results.quality && (
                        <Paper sx={{ p: 3, background: theme.colors.bg.secondary, border: `1px solid ${theme.colors.border.primary}` }}>
                            <Typography sx={{ color: theme.colors.text.quaternary, fontSize: '0.75rem', mb: 1 }}>Raw Analysis Output:</Typography>
                            <pre style={{ color: theme.colors.text.secondary, fontFamily: 'monospace', fontSize: '0.875rem', whiteSpace: 'pre-wrap', wordBreak: 'break-word', margin: 0 }}>
                                {typeof results === 'string' ? results : JSON.stringify(results, null, 2)}
                            </pre>
                        </Paper>
                    )}
                </Box>
            )}

            {/* Cancelled/Failed Status */}
            {(jobStatus === 'cancelled' || jobStatus === 'failed') && (
                <Paper sx={{ p: 3, background: theme.colors.bg.secondary, border: `1px solid ${theme.colors.border.primary}` }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
                        <Box sx={{ width: 40, height: 40, borderRadius: '50%', background: `${getStatusColor(jobStatus)}20`, display: 'flex', alignItems: 'center', justifyContent: 'center', color: getStatusColor(jobStatus) }}>
                            {getStatusIcon(jobStatus)}
                        </Box>
                        <Box>
                            <Typography sx={{ color: theme.colors.text.primary, fontWeight: 600 }}>Analysis {parseJobStatus(jobStatus).label}</Typography>
                            <Typography sx={{ color: theme.colors.text.quaternary, fontSize: '0.75rem' }}>{currentJob?.fileName}</Typography>
                        </Box>
                    </Box>
                    {error && <Alert severity="error" sx={{ background: `${theme.colors.status.error}15` }}>{error}</Alert>}
                </Paper>
            )}

            {/* P9-B: Duplicate Detection Modal */}
            <DuplicateEAModal
                open={showDuplicateModal}
                onClose={() => {
                    setShowDuplicateModal(false);
                    setDuplicateInfo(null);
                }}
                onUseExisting={handleUseExisting}
                onReanalyze={handleReanalyze}
                duplicateInfo={duplicateInfo}
                file={selectedFile ? { name: selectedFile.name, file_type: 'expert' } : null}
                isLoading={isUploading}
            />
        </Box>
    );
});

// PlayArrow icon component
const PlayArrow = () => (
    <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="currentColor">
        <path d="M8 5v14l11-7z"/>
    </svg>
);

export default AdminEAAnalysisDashboard;
